// client/src/components/users/UserForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import userService from '../../services/userService';
import facultyService from '../../services/facultyService';

const UserForm = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const isSelfEdit = isEditMode && user.id.toString() === id;
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'faculty',
    user_type: 'both',
    faculty_id: '',
    is_active: true
  });
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Current language
  const lang = i18n.language;
  
  // Load faculties
  useEffect(() => {
    const loadFaculties = async () => {
      try {
        const response = await facultyService.getFaculties();
        setFaculties(response.data);
      } catch (err) {
        console.error('Error loading faculties', err);
      }
    };
    
    loadFaculties();
  }, []);

  // Load user data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadUser = async () => {
        try {
          setFetchLoading(true);
          const response = await userService.getUser(id);
          
          // Remove password from form data
          const { password, ...userData } = response.data;
          setFormData(userData);
        } catch (err) {
          setError(t('errorLoadingUser'));
          navigate('/users');
        } finally {
          setFetchLoading(false);
        }
      };
      
      loadUser();
    }
  }, [isEditMode, id, navigate, t]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    
    // Handle checkbox (is_active)
    if (name === 'is_active') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Prepare data for submission
      const submissionData = { ...formData };
      
      // Don't send empty password
      if (!submissionData.password) {
        delete submissionData.password;
      }
      
      // In self-edit mode, non-admin users can't change role or faculty
      if (isSelfEdit && !isAdmin()) {
        delete submissionData.role;
        delete submissionData.faculty_id;
        delete submissionData.is_active;
        delete submissionData.user_type;
      }
      
      if (isEditMode) {
        await userService.updateUser(id, submissionData);
        setSuccess(t('userUpdatedSuccess'));
      } else {
        await userService.createUser(submissionData);
        setSuccess(t('userCreatedSuccess'));
        if (!isAdmin()) {
          // Redirect to users list after creating
          setTimeout(() => navigate('/users'), 2000);
        } else {
          // Reset form for admins to allow multiple creations
          setFormData({
            username: '',
            email: '',
            password: '',
            role: 'faculty',
            user_type: 'both',
            faculty_id: '',
            is_active: true
          });
        }
      }
    } catch (err) {
      setError(isEditMode ? t('errorUpdatingUser') : t('errorCreatingUser'));
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? t('editUser') : t('addUser')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="username"
                name="username"
                label={t('username')}
                value={formData.username || ''}
                onChange={handleChange}
                disabled={isEditMode} // Username cannot be changed once created
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                label={t('email')}
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="password"
                name="password"
                label={isEditMode ? t('newPassword') : t('password')}
                type="password"
                value={formData.password || ''}
                onChange={handleChange}
                required={!isEditMode} // Password required for new users
                helperText={isEditMode ? t('leaveBlankToKeepCurrent') : ''}
              />
            </Grid>
            
            {/* Only admins can change roles */}
            {isAdmin() && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="role-label">{t('role')}</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formData.role || 'faculty'}
                    label={t('role')}
                    onChange={handleChange}
                  >
                    <MenuItem value="admin">{t('admin')}</MenuItem>
                    <MenuItem value="faculty">{t('facultyUser')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* User Type Selection - Only for faculty users and only admins can change it */}
            {formData.role === 'faculty' && isAdmin() && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="user-type-label">{t('userType')}</InputLabel>
                  <Select
                    labelId="user-type-label"
                    id="user_type"
                    name="user_type"
                    value={formData.user_type || 'both'}
                    label={t('userType')}
                    onChange={handleChange}
                  >
                    <MenuItem value="maker">{t('maker')}</MenuItem>
                    <MenuItem value="checker">{t('checker')}</MenuItem>
                    <MenuItem value="both">{t('both')}</MenuItem>
                  </Select>
                  <FormHelperText>
                    {t('userTypeHelperText')}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            
            {/* Faculty selection - only shown if role is faculty or for non-admins */}
            {(formData.role === 'faculty' || !isAdmin()) && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="faculty-label">{t('faculty')}</InputLabel>
                  <Select
                    labelId="faculty-label"
                    id="faculty_id"
                    name="faculty_id"
                    value={formData.faculty_id || ''}
                    label={t('faculty')}
                    onChange={handleChange}
                    disabled={!isAdmin() && isSelfEdit} // Non-admins can't change their faculty
                  >
                    <MenuItem value="">{t('selectFaculty')}</MenuItem>
                    {faculties.map(faculty => (
                      <MenuItem key={faculty.id} value={faculty.id}>
                        {faculty[`name_${lang}`]}
                      </MenuItem>
                    ))}
                  </Select>
                  {formData.role === 'faculty' && !formData.faculty_id && (
                    <FormHelperText error>{t('facultyRequired')}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            
            {/* Only admins can change active status */}
            {isAdmin() && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleChange}
                      name="is_active"
                      color="primary"
                    />
                  }
                  label={t('activeAccount')}
                />
              </Grid>
            )}
          </Grid>
          
          <Box sx={{ 
            mt: 3, 
            display: 'flex',
            gap: 2,
            justifyContent: direction === 'ltr' ? 'flex-end' : 'flex-start'
          }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/users')}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? t('saving') : t('save')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default UserForm;